import type { Component } from '../router';
import { navigate } from '../router';
import { api, ApiError } from '../services/api';
import { authInit, setAccessToken } from '../services/auth';
import { mergeDeviceCartOnSignIn } from '../services/cart';
import { takeReturnTo } from '../utils/returnTo';
import { getDeviceId } from '../utils/device';
import { authDeviceClass } from '../utils/viewport';

interface VerifyResponse {
  accessToken: string;
  user: { id: string; email: string; plan?: string; isNew?: boolean };
}

/**
 * The magic-link email built by vfco-auth-package points the browser at
 * `${baseURL}/auth/verify?token=…`. The Portal's POST endpoint is
 * `/api/auth/verify` (JSON body), so this SPA route is the bridge:
 * it reads the token from the URL, exchanges it for the JWT cookie
 * via POST, refreshes the cached `/api/me` snapshot, and sends the
 * user on to `/`. Failure modes (expired link, replay, device cap,
 * network) render an explanatory card in place with a path back to
 * `/login`.
 */

interface ErrorCopy {
  title: string;
  message: string;
  cta: string;
}

/**
 * Maps a verify failure to user-facing copy by error `code` first,
 * then HTTP status. DEVICE_LIMIT_EXCEEDED (409) gets instructional
 * copy instead of a dead-end "invalid link": per the session-cap
 * policy the only way to free a slot is a manual revoke from an
 * already-signed-in device, so the message points there. Deliberately
 * no in-page revoke action — this device is unauthenticated, and that
 * is the security property the cap exists to provide. The copy says a
 * NEW link because the original token is consumed before the cap
 * check (#94), so retrying the same link would 400.
 */
export function verifyErrorCopy(err: unknown): ErrorCopy {
  if (err instanceof ApiError) {
    const body = err.body as { code?: unknown; details?: { maxDevices?: unknown } } | null;
    if (body && typeof body === 'object' && body.code === 'DEVICE_LIMIT_EXCEEDED') {
      const max = body.details?.maxDevices;
      const cap = typeof max === 'number' ? `the maximum of ${max} devices` : 'the maximum number of devices';
      return {
        title: 'Device limit reached',
        message:
          `You're signed in on ${cap}. On a device you're already signed in on, ` +
          'open Settings → Devices, sign one out, then request a new sign-in link.',
        cta: 'Request a new sign-in link',
      };
    }
    if (err.status === 400) {
      return {
        title: 'Sign-in link invalid',
        message: 'This link has expired or already been used. Request a fresh one.',
        cta: 'Back to sign in',
      };
    }
  }
  return {
    title: 'Sign-in link invalid',
    message: 'We could not verify the link. Try requesting a fresh one.',
    cta: 'Back to sign in',
  };
}
export class AuthVerifyPage implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const section = document.createElement('section');
    section.className = 'login';
    const card = document.createElement('div');
    card.className = 'login__card';
    const title = document.createElement('h1');
    title.className = 'login__title';
    title.textContent = 'Signing you in…';
    const sub = document.createElement('p');
    sub.className = 'login__sub';
    sub.textContent = 'One moment while we verify your link.';
    card.append(title, sub);
    section.append(card);
    host.append(section);
    this.root = section;

    const token = new URLSearchParams(location.search).get('token') ?? '';
    if (!token) {
      navigate('/login');
      return;
    }

    void (async () => {
      try {
        // vfco-auth-package verifyReq requires {token, deviceId}; the
        // deviceId must parse as a UUID and is reused across sessions
        // from this browser so the server can recognize the device for
        // session-cap and rotation logic.
        const deviceId = getDeviceId();
        // The server derives the authoritative device_class from User-Agent +
        // viewportWidth and binds it immutably on the session/JWT.
        // deviceClass is sent only as a transitional fallback; the server
        // ignores it. authDeviceClass mirrors the server so the value the SPA
        // later sends on refresh agrees with what the server bound here.
        const resp = await api<VerifyResponse>('/api/auth/verify', {
          method: 'POST',
          body: {
            token,
            deviceId,
            viewportWidth: window.innerWidth,
            deviceClass: authDeviceClass(),
          },
        });
        // Capture the short-lived JWT before authInit() runs so /api/me
        // includes Authorization: Bearer and recognizes the session.
        if (resp?.accessToken) setAccessToken(resp.accessToken);
        await authInit();
        // Fold any anonymous device cart built before this sign-in into the
        // now-authenticated server cart, and AWAIT it before resuming: a return
        // to /checkout would otherwise load the server cart before the merge
        // lands and flash the empty-cart state. Best-effort — a merge failure
        // must not wedge the sign-in, so it's swallowed (the device cart stays
        // for a later retry).
        await mergeDeviceCartOnSignIn().catch(() => undefined);
        // Resume the page the visitor was headed to before the sign-in gate
        // (e.g. /checkout), else land home.
        navigate(takeReturnTo() ?? '/');
      } catch (err) {
        const copy = verifyErrorCopy(err);
        title.textContent = copy.title;
        sub.textContent = copy.message;
        const cta = document.createElement('a');
        cta.className = 'login__submit';
        cta.href = '/login';
        cta.textContent = copy.cta;
        cta.style.textDecoration = 'none';
        cta.style.marginTop = 'var(--space-md)';
        card.append(cta);
      }
    })();
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }
}
